import React, { useState } from 'react';
import { Copy, Search, Loader2, X, Package } from 'lucide-react';
import { orderRegistrationService } from '../../../../services/orderRegistrationService';
import { ordersService } from '../../../../services/ordersService';
import { Order, OrderLine } from '../../../../types/orderRegistration';
import { useWarehouse } from '../../../../contexts/WarehouseContext';

interface CopyOrderDialogProps {
  onSuccess: (newOrderNo: number) => void;
}

export const CopyOrderDialog: React.FC<CopyOrderDialogProps> = ({ onSuccess }) => {
  const { currentWarehouse } = useWarehouse();
  const [searchOrderNo, setSearchOrderNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [foundOrder, setFoundOrder] = useState<Order | null>(null);
  const [foundOrderLines, setFoundOrderLines] = useState<OrderLine[]>([]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    const orderNo = parseInt(searchOrderNo);
    if (isNaN(orderNo) || orderNo <= 0) {
      setError('Ugyldig ordrenummer');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFoundOrder(null);
      setFoundOrderLines([]);

      // Fetch order with lines using existing service
      const ordersData = await ordersService.getNewOrdersWithLines(currentWarehouse || undefined);
      const orderWithLines = ordersData.find(o => o.header.orderNo === orderNo);

      if (!orderWithLines) {
        setError(`Ordre ${orderNo} ikke funnet`);
        return;
      }

      // Convert to Order and OrderLine types
      const order: Order = {
        ...orderWithLines.header,
        id: orderWithLines.header.id,
        companyNo: orderWithLines.header.companyNo,
        orderNo: orderWithLines.header.orderNo,
        orderNoERP: orderWithLines.header.orderNoERP,
        orderStatus: orderWithLines.header.orderStatus,
        customerNo: orderWithLines.header.customerNo,
        statusText: orderWithLines.header.statusText
      };

      const lines: OrderLine[] = orderWithLines.lines.map(line => ({
        ...line,
        id: line.id,
        companyNo: line.companyNo,
        orderNo: line.orderNo,
        lineNo: line.lineNo,
        lineStatus: line.lineStatus,
        itemNo: line.itemNo,
        statusText: line.statusText
      }));

      setFoundOrder(order);
      setFoundOrderLines(lines);
    } catch (err) {
      console.error('Error searching for order:', err);
      setError(err instanceof Error ? err.message : 'Kunne ikke finne ordre');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!foundOrder) return;

    try {
      setCopying(true);
      setError(null);

      const response = await orderRegistrationService.copyOrder(foundOrder.orderNo);

      if (response.success) {
        alert(`Ordre kopiert! Nytt ordrenummer: ${response.newOrderNo}`);
        onSuccess(response.newOrderNo);

        // Reset form
        setSearchOrderNo('');
        setFoundOrder(null);
        setFoundOrderLines([]);
      } else {
        throw new Error(response.message || 'Kunne ikke kopiere ordre');
      }
    } catch (err) {
      console.error('Error copying order:', err);
      setError(err instanceof Error ? err.message : 'Kunne ikke kopiere ordre. Prøv igjen.');
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Search Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Søk etter ordre</h3>
        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ordrenummer
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                value={searchOrderNo}
                onChange={(e) => setSearchOrderNo(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Skriv inn ordrenummer..."
                min="1"
              />
              <button
                type="submit"
                disabled={loading || !searchOrderNo}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Søker...
                  </>
                ) : (
                  <>
                    <Search size={20} />
                    Søk
                  </>
                )}
              </button>
            </div>
          </div>
        </form>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
          </div>
        )}
      </div>

      {/* Order Details */}
      {foundOrder && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Ordre funnet</h3>
            <button
              onClick={handleCopy}
              disabled={copying}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg transition-colors font-medium"
            >
              {copying ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Kopierer...
                </>
              ) : (
                <>
                  <Copy size={20} />
                  Kopier Ordre
                </>
              )}
            </button>
          </div>

          {/* Order Header Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <span className="text-sm font-medium text-gray-500">Ordrenummer:</span>
              <div className="text-base font-semibold text-gray-900">{foundOrder.orderNo}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Ordrenr ERP:</span>
              <div className="text-base text-gray-900">{foundOrder.orderNoERP}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Kundenummer:</span>
              <div className="text-base text-gray-900">{foundOrder.customerNo}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Kundenavn:</span>
              <div className="text-base text-gray-900">{foundOrder.customerName || '-'}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Lager:</span>
              <div className="text-base text-gray-900">{foundOrder.warehouse || '-'}</div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Leveringsdato:</span>
              <div className="text-base text-gray-900">
                {orderRegistrationService.formatDate(foundOrder.deliveryDate)}
              </div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Status:</span>
              <div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {foundOrder.statusText}
                </span>
              </div>
            </div>
          </div>

          {/* Order Lines */}
          {foundOrderLines.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package size={16} />
                Ordrelinjer ({foundOrderLines.length})
              </h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Linje
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Varenr
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Beskrivelse
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                        Antall
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Enhet
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Lokasjon
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {foundOrderLines.map((line) => (
                      <tr key={line.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-900">{line.lineNo}</td>
                        <td className="px-3 py-2 text-sm font-mono text-gray-900">{line.itemNo}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{line.description1 || '-'}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-900 font-medium">
                          {orderRegistrationService.formatQuantity(line.quantity)}
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900">{line.itemUnitCode || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-900">{line.stdLocationName || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Box */}
      {!foundOrder && !error && !loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Search className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Slik kopierer du en ordre</p>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>Skriv inn ordrenummeret du ønsker å kopiere</li>
                <li>Klikk på "Søk" for å finne ordren</li>
                <li>Gjennomgå ordreinformasjonen</li>
                <li>Klikk på "Kopier Ordre" for å lage en kopi</li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
